import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_ATTEMPTS = 10;
const POLL_MS = 2000;

export default function BillingSuccess() {
  const [sp] = useSearchParams();
  const [state, setState] = useState({ status: "checking", payment_status: null, amount: null, currency: null });
  const navigate = useNavigate();
  const sessionId = sp.get("session_id");

  const poll = useCallback(async (attempts = 0) => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id" });
      return;
    }
    if (attempts >= MAX_ATTEMPTS) {
      setState({ status: "timeout" });
      return;
    }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        trackEvent("purchase", { transaction_id: sessionId, value: (data.amount_total || 0) / 100, currency: data.currency || "usd" });
        setState({ status: "paid", payment_status: "paid", amount: data.amount_total, currency: data.currency });
        return;
      }
      if (data.status === "expired") {
        setState({ status: "expired" });
        return;
      }
      setTimeout(() => poll(attempts + 1), POLL_MS);
    } catch (e) {
      setState({ status: "error", message: e.message });
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="bg-white text-[#37352F] min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full text-center">
        {state.status === "checking" && (
          <div data-testid="billing-checking">
            <Loader2 className="w-10 h-10 animate-spin text-[#2383E2] mx-auto mb-6" />
            <h1 className="font-serif-h text-2xl font-semibold mb-2">Confirming your payment…</h1>
            <p className="text-[#787774]">Just a moment — verifying with Stripe.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div data-testid="billing-success">
            <span className="notion-emoji-box callout-green mx-auto mb-6" style={{ width: 64, height: 64, fontSize: 32 }}>🎉</span>
            <h1 className="font-serif-h text-3xl font-semibold mb-3">Welcome to Pro!</h1>
            <p className="text-[#787774] mb-6">Your account has been upgraded. You now have Pro access for the next 30 days.</p>
            <button
              onClick={() => { window.location.href = "/dashboard"; }}
              data-testid="go-dashboard-btn"
              className="bg-[#2383E2] text-white font-medium px-5 py-3 rounded-md flex items-center gap-2 hover:bg-[#1A6FCC] mx-auto"
            >
              Go to dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <div data-testid="billing-failed">
            <span className="notion-emoji-box callout-red mx-auto mb-6" style={{ width: 64, height: 64, fontSize: 32 }}>😕</span>
            <h1 className="font-serif-h text-2xl font-semibold mb-3">Payment {state.status === "expired" ? "expired" : "not confirmed"}</h1>
            <p className="text-[#787774] mb-6">
              {state.status === "timeout" ? "It's taking longer than usual. Check your email or try again — you weren't charged." : "The session ended before completing. You can try again — no charge was applied."}
            </p>
            <Link to="/pricing" className="text-[#2383E2] hover:underline font-medium">← Back to pricing</Link>
          </div>
        )}
      </div>
    </div>
  );
}
